import { CAMINO } from '@somnhold/content';
import type { LitterRules, SnowRules } from '../types.ts';
import { base } from './litter-rules.ts';
import type { ColLayout, CorralLayout, FrostLayout } from './nieve-layout.ts';

/**
 * The stretcher's rules for the cleric's chapter 3 (docs/sendas/clerigo.md, «La nieve»; content CAMINO.cold, col, frost,
 * corral), from their layouts (battle/nieve-layout.ts): the fight's builder (campaign/sendas.ts) and the renderer's
 * scenes build them the same way. Life in hit points worked out by the caller (seconds of D).
 */

const cold = (): SnowRules['cold'] => ({ ...CAMINO.cold });

export const colRules = (L: ColLayout): LitterRules => {
  const C = CAMINO.col;
  return {
    ...base(),
    kind: 'col',
    pace: C.pace,
    way: L.way.map((p) => ({ ...p })),
    dens: [],
    foes: null,
    crows: null,
    limpet: null,
    mirrors: null,
    snow: {
      cold: cold(),
      braziers: L.chapels.map((c) => ({ x: c.x, y: c.y, lit: false, stand: { ...c.stand } })),
      wolf: { first: C.wolf.first, every: C.wolf.every, n: [C.wolf.n[0], C.wolf.n[1]], grow: 0 },
      dens: L.dens.map((d) => ({ ...d })),
      behind: C.behind,
      frost: null,
      corral: null,
    },
  };
};

export const frostRules = (L: FrostLayout, burn: number): LitterRules => {
  const F = CAMINO.frost;
  return {
    ...base(),
    kind: 'frost',
    way: [{ ...L.rest }],
    dens: [],
    foes: null,
    crows: null,
    limpet: null,
    mirrors: null,
    snow: {
      cold: cold(),
      braziers: [],
      wolf: { first: F.wolf.first, every: F.wolf.every, n: [1, 1], grow: F.wolf.grow },
      dens: L.dens.map((d) => ({ ...d })),
      behind: 0,
      frost: {
        lantern: { ...L.lantern },
        door: { ...L.door },
        mirrors: L.mirrors.map((m, i) => ({ q: m.q, r: m.r, stand: { ...(L.mirrorStand[i] as { x: number; y: number }) } })),
        bases: L.bases.map((b) => ({ ...b })),
        thaws: L.thaws.map((t) => [...t]),
        walls: L.walls.map((w) => ({ ...w })),
        radius: L.radius,
        pile: { ...L.pile },
        start: [...L.start0],
        carryBp: F.carryBp,
        take: F.take,
        thaw: F.thaw,
        refreeze: F.refreeze,
        stand: F.stand,
        reach: F.reach,
        open: F.open,
        drop: F.drop,
        burn,
      },
      corral: null,
    },
  };
};

export const corralRules = (L: CorralLayout, watcherHp: number): LitterRules => {
  const C = CAMINO.corral;
  const ids = ['pietMother', 'kidA', 'kidB'];
  return {
    ...base(),
    kind: 'corral',
    way: [{ ...L.rest }],
    dens: [],
    foes: null,
    crows: null,
    limpet: null,
    mirrors: null,
    snow: {
      cold: cold(),
      braziers: L.braziers.map((b) => ({ ...b, lit: true })),
      wolf: { first: C.wolf.first, every: C.wolf.every, n: [C.wolf.n[0], C.wolf.n[1]], grow: 0 },
      dens: L.dens.map((d) => ({ ...d })),
      behind: 0,
      frost: null,
      corral: {
        night: C.night,
        moth: { ...C.moth },
        watcher: { hp: watcherHp, first: C.watcher.first, every: C.watcher.every, warn: C.watcher.warn, r: C.watcher.r, heroBp: C.watcher.heroBp, blows: C.watcher.blows, down: C.watcher.down, downHeld: C.watcher.downHeld },
        freezeR: C.freezeR,
        drop: C.drop,
        family: L.family.map((f, i) => ({ id: ids[i] as string, x: f.x, y: f.y })),
        sky: L.sky.map((p) => ({ ...p })),
      },
    },
  };
};
